import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate, Link } from 'react-router-dom';
const ITEMS_PER_PAGE = 10;

export default function ExpertDashboard() {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    
    // Pagination states
    const [energyPage, setEnergyPage] = useState(1);
    
    // Theme state
    const [theme, setTheme] = useState(localStorage.getItem('expert-theme') || 'dark');
    
    // Modal state
    const [reviewModal, setReviewModal] = useState(null); // { type: 'approve' | 'reject', asset: {} }
    const [note, setNote] = useState('');

    // Bulk selection states
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [bulkApproveModal, setBulkApproveModal] = useState(false);
    const [bulkRejectModal, setBulkRejectModal] = useState(false);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [bulkNote, setBulkNote] = useState('');

    const navigate = useNavigate();

    const fetchPending = async () => {
        try {
            const res = await api.get('/api/expert/pending-attestations');
            // axios wraps response in .data
            const data = res.data ?? res;
            setPending(Array.isArray(data) ? data : []);
            setLoading(false);
        } catch (err) {
            console.error('Fetch failed:', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Technical Verification | Silicon Himalayas";
        fetchPending();
        const interval = setInterval(fetchPending, 10000); // 10s polling
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setSelectedAssetIds([]);
    }, [energyPage, pending]);

    const handleSubmit = async () => {
        if (!reviewModal || submitting) return;
        const { type, asset } = reviewModal;
        setSubmitting(true);
        
        try {
            if (type === 'approve') {
                await api.post(`/api/expert/approve/${asset.id}`, { notes: note });
                setMessage(`✓ Attestation approved and committed to ledger.`);
            } else {
                await api.post(`/api/expert/reject/${asset.id}`, { reason: note });
                setMessage(`✗ Attestation rejected and recorded on ledger.`);
            }
            
            setReviewModal(null);
            setNote('');
            // Give Fabric 1.5s to commit before querying ledger state
            setTimeout(() => {
                fetchPending();
                setTimeout(() => setMessage(''), 5000);
            }, 1500);
        } catch (err) {
            const errMsg = err.response?.data?.error || err.message || 'Transaction failed';
            setError(errMsg);
            setTimeout(() => setError(''), 8000);
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulkSubmit = async (type) => {
        if (selectedAssetIds.length === 0 || bulkSubmitting) return;
        setBulkSubmitting(true);
        let successCount = 0;
        let failCount = 0;

        try {
            for (const id of selectedAssetIds) {
                try {
                    if (type === 'approve') {
                        await api.post(`/api/expert/approve/${id}`, { notes: bulkNote });
                    } else {
                        await api.post(`/api/expert/reject/${id}`, { reason: bulkNote });
                    }
                    successCount++;
                } catch (err) {
                    console.error(`Bulk action failed for asset ID ${id}:`, err);
                    failCount++;
                }
            }
            setMessage(`Bulk action complete. ${successCount} processed, ${failCount} failed.`);
            setSelectedAssetIds([]);
            setBulkNote('');
            setBulkApproveModal(false);
            setBulkRejectModal(false);
            setTimeout(() => {
                fetchPending();
                setTimeout(() => setMessage(''), 5000);
            }, 1500);
        } catch (err) {
            console.error("Bulk expert submit error:", err);
        } finally {
            setBulkSubmitting(false);
        }
    };

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('expert-theme', newTheme);
    };

    const energyAll = pending.filter(a => !a.isPhysical);

    const energyPending = energyAll.slice((energyPage - 1) * ITEMS_PER_PAGE, energyPage * ITEMS_PER_PAGE);

    const energyTotalPages = Math.ceil(energyAll.length / ITEMS_PER_PAGE);

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-[#080a0e] text-emerald-500 font-black tracking-widest animate-pulse">
                SYNCHRONIZING VERIFICATION TERMINAL...
            </div>
        );
    }

    return (
        <div className={`min-h-screen transition-colors duration-500 ${theme === 'dark' ? 'bg-[#080a0e] text-slate-200' : 'bg-slate-50 text-slate-600'} p-4 sm:p-8 font-sans`}>
            {/* Header */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 max-w-7xl mx-auto animate-fade-in">
                <Link to="/" className="flex items-center gap-4 group transition-all">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <svg className="w-8 h-8 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 20L8 8L13 16" />
                                    <path d="M8 20L15 6L22 20" />
                                    <path d="M8 8L15 6" strokeDasharray="1.5 1.5" />
                                    <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                                </svg>
                            </div>
                            <h1 className={`text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Technical Verification <span className="text-emerald-500 italic">Terminal</span></h1>
                        </div>
                        <p className="text-slate-500 text-[10px] mt-1 ml-13 uppercase tracking-[0.3em] font-black group-hover:text-emerald-500 transition-colors">Generation Data Review · Silicon Himalayas Committee</p>
                    </div>
                </Link>
                <div className="flex flex-wrap gap-4 w-full md:w-auto">
                    <button 
                        onClick={toggleTheme}
                        className={`p-3 rounded-2xl border transition-all flex items-center justify-center gap-2 group flex-1 sm:flex-initial ${theme === 'dark' ? 'bg-white/5 border-white/10 text-yellow-400 hover:bg-white/10' : 'bg-slate-200/50 border-slate-200 text-slate-800 hover:bg-slate-200'}`}
                    >
                        {theme === 'dark' ? (
                            <svg className="w-5 h-5 group-hover:rotate-12 transition-transform" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"></path></svg>
                        ) : (
                            <svg className="w-5 h-5 group-hover:-rotate-12 transition-transform" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path></svg>
                        )}
                        <span className="text-[10px] font-black uppercase tracking-widest">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <button onClick={handleLogout} className={`px-5 py-2.5 bg-white/5 hover:bg-white/10 text-slate-400 border rounded-2xl transition font-bold text-xs uppercase tracking-widest flex-1 sm:flex-initial ${theme === 'dark' ? 'border-white/10 shadow-none' : 'border-slate-200 shadow-sm'}`}>Logout</button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto space-y-10">
                <section className={`rounded-[2.5rem] border p-6 shadow-2xl animate-fade-in transition-all duration-300 ${theme === 'dark' ? 'glass border-white/5' : 'bg-white border-slate-200'}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                            { 
                                label: 'Pending Review', 
                                value: pending.length,
                                color: pending.length > 0 ? 'text-orange-400' : 'text-slate-500',
                                glow: pending.length > 0
                            },
                            { 
                                label: 'Energy Queue', 
                                value: energyAll.length,
                                color: 'text-emerald-400'
                            },
                            { 
                                label: 'Platform', 
                                value: 'NRB Sandbox',
                                color: 'text-indigo-400',
                                small: true
                            },
                            { 
                                label: 'Standard', 
                                value: 'I-REC(E)',
                                color: 'text-cyan-400',
                                small: true
                            }
                        ].map((s, i) => (
                            <div key={i} className={`rounded-2xl p-4 text-center border transition-all ${
                                s.glow 
                                    ? (theme === 'dark' ? 'bg-orange-500/5 border-orange-500/20 ring-1 ring-orange-500/10' : 'bg-orange-50/50 border-orange-200 ring-1 ring-orange-100')
                                    : (theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200/80')
                            }`}>
                                <p className={`font-black tracking-tighter mb-1 ${s.small ? 'text-sm' : 'text-3xl'} ${s.color}`}>
                                    {s.value}
                                </p>
                                <p className={`text-[9px] uppercase tracking-widest font-bold ${theme === 'dark' ? 'text-slate-500' : 'text-slate-550'}`}>
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                {(message || error) && (
                    <div className={`${error ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'} p-4 rounded-3xl mb-8 border flex items-center gap-3 animate-slide-in`}>
                        <div className={`w-2 h-2 rounded-full animate-pulse ${error ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                        <span className="font-bold text-sm tracking-wide uppercase">{message || error}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-10">
                    {/* Energy Queue */}
                    <section className={`${theme === 'dark' ? 'glass border-white/5 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'} rounded-[2.5rem] border overflow-hidden transition-all duration-300`}>
                        <div className={`p-8 ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'} border-b flex justify-between items-center`}>
                            <h2 className={`text-xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Generation Data <span className="text-slate-500">Queue</span></h2>
                            <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Total: {energyAll.length}</span>
                        </div>
                        {energyAll.length > 0 && (
                            <div className={`px-8 py-4 ${theme === 'dark' ? 'bg-white/2 border-white/5' : 'bg-slate-50/50 border-slate-100'} border-b flex justify-between items-center flex-wrap gap-4`}>
                                <div className="flex items-center gap-3">
                                    <input 
                                        type="checkbox" 
                                        checked={energyPending.length > 0 && energyPending.every(a => selectedAssetIds.includes(a.id))}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const visibleIds = energyPending.map(a => a.id);
                                                setSelectedAssetIds(prev => [...new Set([...prev, ...visibleIds])]);
                                            } else {
                                                const visibleIds = energyPending.map(a => a.id);
                                                setSelectedAssetIds(prev => prev.filter(id => !visibleIds.includes(id)));
                                            }
                                        }}
                                        className="rounded border-slate-350 text-emerald-500 focus:ring-emerald-500 bg-[#0d1117]/20 cursor-pointer w-4 h-4"
                                    />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Select Page</span>
                                    {selectedAssetIds.length > 0 && (
                                        <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3.5 py-1 rounded-full">
                                            {selectedAssetIds.length} Selected
                                        </span>
                                    )}
                                </div>
                                {selectedAssetIds.length > 0 && (
                                    <div className="flex gap-2 items-center">
                                        <button 
                                            onClick={() => {
                                                setBulkNote('');
                                                setBulkApproveModal(true);
                                            }}
                                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl text-[10px] uppercase tracking-widest transition-all"
                                        >
                                            Bulk Approve
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setBulkNote('');
                                                setBulkRejectModal(true);
                                            }}
                                            className="px-4 py-2 bg-rose-500/25 hover:bg-rose-500/35 text-rose-455 border border-rose-500/30 font-bold rounded-xl text-[10px] uppercase tracking-widest transition-all"
                                        >
                                            Bulk Reject
                                        </button>
                                        <button 
                                            onClick={() => setSelectedAssetIds([])}
                                            className="px-3 py-2 text-xs text-slate-500 hover:text-slate-300 underline font-semibold transition"
                                        >
                                            Deselect
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="p-2">
                            {energyPending.map(a => (
                                <div key={a.id} className={`p-6 transition group rounded-[2rem] border mb-2 ${theme === 'dark' ? 'hover:bg-white/5 border-transparent hover:border-white/5' : 'hover:bg-slate-50 border-transparent hover:border-slate-200'}`}>
                                    <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-4">
                                        <div className="flex items-start gap-4">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedAssetIds.includes(a.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedAssetIds(prev => [...prev, a.id]);
                                                    } else {
                                                        setSelectedAssetIds(prev => prev.filter(id => id !== a.id));
                                                    }
                                                }}
                                                className="mt-1.5 rounded border-slate-350 text-emerald-500 focus:ring-emerald-500 bg-[#0d1117]/20 cursor-pointer w-4 h-4"
                                            />
                                            <div>
                                                <p className="text-2xl font-black text-emerald-500 tracking-tighter">{a.mwh} <span className="text-xs text-slate-600 font-bold uppercase tracking-widest">MWh</span></p>
                                                <p className="text-[10px] font-mono text-slate-500 mt-1 uppercase tracking-widest font-bold">ID: {a.id}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-xs font-bold ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>Plant: {a.projectId}</p>
                                            <p className="text-[10px] font-mono text-slate-500 mt-1">{new Date(a.timestamp).toLocaleString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-6 text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-4">
                                        <span>
                                            Fuel: {a.fuelType || 'Solar'}
                                        </span>
                                        <span>
                                            Technology: {a.technologyType || 'Photovoltaic'}
                                        </span>
                                        <span>
                                            Vintage: {a.vintagePeriod || new Date(a.timestamp).toLocaleDateString('en-US', {month:'short', year:'numeric'})}
                                        </span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button onClick={() => setReviewModal({ type: 'approve', asset: a })} className="flex-1 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition transform active:scale-95 w-full">Approve</button>
                                        <button onClick={() => setReviewModal({ type: 'reject', asset: a })} className={`px-6 py-3 bg-rose-500/5 hover:bg-rose-500/10 text-rose-500/40 hover:text-rose-500 border rounded-2xl text-[10px] font-black uppercase tracking-widest transition active:scale-95 w-full ${theme === 'dark' ? 'border-white/5 hover:border-rose-500/20' : 'border-slate-200 hover:border-rose-300'}`}>Reject</button>
                                    </div>
                                </div>
                            ))}
                            {energyPending.length === 0 && <div className="p-20 text-center text-slate-500 font-bold uppercase tracking-widest text-xs italic">No pending submissions. Generation data will appear here once sensors report new readings.</div>}
                        </div>
                        
                        {/* Pagination controls for Energy */}
                        {energyTotalPages > 1 && (
                            <div className={`p-6 ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-50'} border-t ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-center items-center gap-6`}>
                                <button onClick={() => setEnergyPage(p => Math.max(1, p-1))} disabled={energyPage === 1} className="p-2 hover:bg-emerald-500/10 disabled:opacity-30 transition rounded-lg text-emerald-500">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7"></path></svg>
                                </button>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Page {energyPage} <span className="mx-2 opacity-30">/</span> {energyTotalPages}</span>
                                <button onClick={() => setEnergyPage(p => Math.min(energyTotalPages, p+1))} disabled={energyPage === energyTotalPages} className="p-2 hover:bg-emerald-500/10 disabled:opacity-30 transition rounded-lg text-emerald-500">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7"></path></svg>
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </main>

            {/* Bulk Approve Modal */}
            {bulkApproveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-fade-in shadow-2xl">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => !bulkSubmitting && setBulkApproveModal(false)}></div>
                    <div className={`${theme === 'dark' ? 'glass border-white/10' : 'bg-white border-slate-200'} w-full max-w-lg rounded-xl border relative z-10 shadow-2xl animate-scale-in`}>
                        <div className={`p-8 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-between items-center`}>
                            <div>
                                <h3 className={`text-xl font-black uppercase tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Bulk Verify Generation Data</h3>
                                <p className="text-xs text-slate-500 mt-1 font-mono uppercase tracking-[0.2em] font-bold">Verifying {selectedAssetIds.length} Assets</p>
                            </div>
                            <button onClick={() => !bulkSubmitting && setBulkApproveModal(false)} className="p-2 hover:bg-white/10 rounded-full transition text-slate-500"><svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                        </div>
                        <div className="p-8">
                            <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-4 ml-1">
                                Verification Notes (Applied to all)
                            </label>
                            <textarea 
                                value={bulkNote}
                                onChange={e => setBulkNote(e.target.value)}
                                placeholder="Confirm generation data is consistent with plant meter records and sensor readings..."
                                className={`w-full ${theme === 'dark' ? 'bg-[#0d1117] border-white/5' : 'bg-slate-50 border-slate-200'} border rounded-xl p-4 text-sm focus:outline-none focus:border-emerald-500/50 h-32 resize-none transition-all placeholder:text-slate-400 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-900'} font-medium shadow-inner`}
                            />
                            <div className="mt-8 flex gap-4">
                                <button onClick={() => !bulkSubmitting && setBulkApproveModal(false)} disabled={submitting} className={`flex-1 py-4 ${theme === 'dark' ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'} font-black uppercase tracking-widest text-[10px] rounded-xl transition disabled:opacity-50`}>Abort</button>
                                <button 
                                    onClick={() => handleBulkSubmit('approve')}
                                    disabled={bulkSubmitting}
                                    className="flex-2 px-10 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black uppercase tracking-[0.2em] text-[10px] shadow-lg transition active:scale-95 disabled:opacity-50"
                                >
                                    {bulkSubmitting ? 'Committing to ledger...' : 'Approve & Commit to Ledger'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Reject Modal */}
            {bulkRejectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-fade-in shadow-2xl">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => !bulkSubmitting && setBulkRejectModal(false)}></div>
                    <div className={`${theme === 'dark' ? 'glass border-white/10' : 'bg-white border-slate-200'} w-full max-w-lg rounded-xl border relative z-10 shadow-2xl animate-scale-in`}>
                        <div className={`p-8 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-between items-center`}>
                            <div>
                                <h3 className={`text-xl font-black uppercase tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Bulk Reject Generation Data</h3>
                                <p className="text-xs text-slate-500 mt-1 font-mono uppercase tracking-[0.2em] font-bold">Rejecting {selectedAssetIds.length} Assets</p>
                            </div>
                            <button onClick={() => !bulkSubmitting && setBulkRejectModal(false)} className="p-2 hover:bg-white/10 rounded-full transition text-slate-500"><svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                        </div>
                        <div className="p-8">
                            <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-4 ml-1">
                                Rejection Reason (Applied to all)
                            </label>
                            <textarea 
                                value={bulkNote}
                                onChange={e => setBulkNote(e.target.value)}
                                placeholder="Describe the data inconsistency or missing information that requires correction..."
                                className={`w-full ${theme === 'dark' ? 'bg-[#0d1117] border-white/5' : 'bg-slate-50 border-slate-200'} border rounded-xl p-4 text-sm focus:outline-none focus:border-rose-500/50 h-32 resize-none transition-all placeholder:text-slate-400 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-900'} font-medium shadow-inner`}
                            />
                            <div className="mt-8 flex gap-4">
                                <button onClick={() => !bulkSubmitting && setBulkRejectModal(false)} disabled={submitting} className={`flex-1 py-4 ${theme === 'dark' ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'} font-black uppercase tracking-widest text-[10px] rounded-xl transition disabled:opacity-50`}>Abort</button>
                                <button 
                                    onClick={() => handleBulkSubmit('reject')}
                                    disabled={bulkSubmitting}
                                    className="flex-2 px-10 py-4 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-black uppercase tracking-[0.2em] text-[10px] shadow-lg transition active:scale-95 disabled:opacity-50"
                                >
                                    {bulkSubmitting ? 'Requesting resubmission...' : 'Reject & Request Resubmission'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Micro-Auditing Modal */}
            {reviewModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-fade-in shadow-2xl">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => !submitting && setReviewModal(null)}></div>
                    <div className={`${theme === 'dark' ? 'glass border-white/10' : 'bg-white border-slate-200'} w-full max-w-lg rounded-[2.5rem] border relative z-10 shadow-2xl animate-scale-in`}>
                        <div className={`p-8 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-between items-center`}>
                            <div>
                                <h3 className={`text-xl font-black uppercase tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{reviewModal.type === 'approve' ? 'Verify Generation Data' : 'Flag Data Issue'}</h3>
                                <p className="text-[10px] text-slate-500 mt-1 font-mono uppercase tracking-[0.2em] font-bold">Transaction: {reviewModal.asset.id}</p>
                            </div>
                            <button onClick={() => !submitting && setReviewModal(null)} className="p-2 hover:bg-white/10 rounded-full transition text-slate-500"><svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                        </div>
                        <div className="p-8">
                            {/* Asset Detail Data Points */}
                            <div className={`grid grid-cols-2 gap-4 p-5 mb-6 rounded-xl border text-xs font-medium ${theme === 'dark' ? 'bg-[#0d1117]/50 border-white/5 text-slate-300' : 'bg-slate-50 border-slate-200/80 text-slate-650'}`}>
                                <div>
                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Source Project</span>
                                    <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{reviewModal.asset.projectId}</span>
                                </div>
                                <div>
                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Energy Volume</span>
                                    <span className="text-sm font-semibold text-emerald-500">{reviewModal.asset.mwh} MWh</span>
                                </div>
                                <div>
                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Vintage Period</span>
                                    <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{reviewModal.asset.vintagePeriod || new Date(reviewModal.asset.timestamp).toLocaleDateString('en-US', {month:'short', year:'numeric'})}</span>
                                </div>
                                <div>
                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Fuel / Technology</span>
                                    <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{reviewModal.asset.fuelType || 'Solar'} / {reviewModal.asset.technologyType || 'Photovoltaic'}</span>
                                </div>
                                <div className="col-span-2">
                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Asset ID / Ledger Hash</span>
                                    <span className="font-mono text-[11px] block break-all opacity-85 select-all">{reviewModal.asset.id}</span>
                                </div>
                                <div className="col-span-2">
                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Reading Timestamp</span>
                                    <span className={`text-[11px] font-bold ${theme === 'dark' ? 'text-slate-350' : 'text-slate-600'}`}>{new Date(reviewModal.asset.timestamp).toLocaleString()}</span>
                                </div>
                            </div>

                            <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-4 ml-1">
                                {reviewModal.type === 'approve' ? 'Verification Notes' : 'Rejection Reason'}
                            </label>
                            <textarea 
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder={reviewModal.type === 'approve' ? "Confirm generation data is consistent with plant meter records and sensor readings..." : "Describe the data inconsistency or missing information that requires correction..."}
                                className={`w-full ${theme === 'dark' ? 'bg-[#0d1117] border-white/5' : 'bg-slate-50 border-slate-200'} border rounded-2xl p-4 text-sm focus:outline-none focus:border-emerald-500/50 h-32 resize-none transition-all placeholder:text-slate-400 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-900'} font-medium shadow-inner`}
                            />
                            <div className="mt-8 flex gap-4">
                                <button onClick={() => !submitting && setReviewModal(null)} disabled={submitting} className={`flex-1 py-4 ${theme === 'dark' ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'} font-black uppercase tracking-widest text-[10px] rounded-2xl transition disabled:opacity-50`}>Abort</button>
                                <button 
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className={`flex-2 px-10 py-4 ${reviewModal.type === 'approve' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-rose-600 hover:bg-rose-500'} text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] shadow-lg transition active:scale-95 disabled:opacity-50`}
                                >
                                    {submitting ? 'Committing to ledger...' : (reviewModal.type === 'approve' ? 'Approve & Commit to Ledger' : 'Reject & Request Resubmission')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
